import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, LogOut, Download, RefreshCw, Users, Mail, Megaphone, Rocket, PenLine, Trash2, Inbox, Send, LayoutDashboard, Mails, Eye, EyeOff } from "lucide-react";
import PostBody from "@/components/site/PostBody";
import { renderInline } from "@/components/site/inlineFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hha_admin_token";

function AnnounceCard({
  cardTestId,
  btnPrefix,
  Icon,
  title,
  description,
  pending,
  ctaLabel,
  isConfirming,
  onStartConfirm,
  onCancel,
  onConfirm,
  sending,
}) {
  const noneLeft = pending === 0;
  return (
    <div className="rounded-2xl border border-gold/25 bg-gold/[0.06] p-6" data-testid={cardTestId}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-gold/15 text-gold">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="font-display text-lg font-semibold text-cream">{title}</p>
            <p className="mt-1 font-body text-sm text-slate-400">
              {description}{" "}
              <span className="text-gold">{pending} waiting to be notified</span>.
            </p>
          </div>
        </div>

        {!isConfirming ? (
          <button
            onClick={onStartConfirm}
            disabled={noneLeft}
            data-testid={`${btnPrefix}-button`}
            className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-medium text-twilight transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_20px_rgba(251,191,36,0.45)] disabled:opacity-40"
          >
            <Icon className="h-4 w-4" />
            {noneLeft ? "All notified" : ctaLabel}
          </button>
        ) : (
          <div className="flex flex-shrink-0 flex-col items-stretch gap-2 sm:items-end">
            <p className="font-body text-sm text-cream">Send to {pending} subscriber(s)?</p>
            <div className="flex gap-2">
              <button
                onClick={onCancel}
                disabled={sending}
                data-testid={`${btnPrefix}-cancel`}
                className="rounded-full border border-white/15 px-5 py-2 font-body text-sm text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={sending}
                data-testid={`${btnPrefix}-confirm`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2 font-medium text-twilight transition-[background-color] duration-300 hover:bg-gold-light disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                Yes, send now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState(null);
  const [announcing, setAnnouncing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newPost, setNewPost] = useState({ title: "", body: "" });
  const [notifyOnPublish, setNotifyOnPublish] = useState(false);
  const [posting, setPosting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testTemplate, setTestTemplate] = useState("launchweek");
  const [sendingTest, setSendingTest] = useState(false);
  const [tab, setTab] = useState("overview");
  const [previewMode, setPreviewMode] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  }, []);

  const loadExtras = useCallback(async (authToken) => {
    const cfg = { headers: { Authorization: `Bearer ${authToken}` } };
    try {
      const [b, m] = await Promise.all([
        axios.get(`${API}/blog`),
        axios.get(`${API}/admin/messages`, cfg),
      ]);
      setPosts(b.data);
      setMessages(m.data.messages);
    } catch {
      /* handled by subscribers loader */
    }
  }, []);

  const createPost = async () => {
    if (!newPost.title.trim() || !newPost.body.trim()) {
      toast.error("Please add a title and body.");
      return;
    }
    setPosting(true);
    try {
      const res = await axios.post(
        `${API}/admin/blog`,
        { ...newPost, notify_subscribers: notifyOnPublish },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const n = res.data?.notified || 0;
      toast.success(
        notifyOnPublish
          ? `Post published! ${n} subscriber${n === 1 ? "" : "s"} notified.`
          : "Post published!"
      );
      setNewPost({ title: "", body: "" });
      setNotifyOnPublish(false);
      loadExtras(token);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); }
      else toast.error("Could not publish. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  const deletePost = async (id) => {
    try {
      await axios.delete(`${API}/admin/blog/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Post deleted.");
      loadExtras(token);
    } catch {
      toast.error("Could not delete the post.");
    }
  };

  const [notifyingPostId, setNotifyingPostId] = useState(null);
  const resendBlogNotify = async (post) => {
    if (!window.confirm(
      `Re-send "${post.title}" to all ${data?.count ?? "your"} subscribers?\n\n` +
      `Anyone who received the original notification will get a duplicate — this is meant for reaching people who were missed.`
    )) return;
    setNotifyingPostId(post.id);
    try {
      const res = await axios.post(
        `${API}/admin/blog/${post.id}/notify`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || `Notifying ${res.data.total} subscribers…`);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); }
      else toast.error("Could not resend notification.");
    } finally {
      setNotifyingPostId(null);
    }
  };

  const loadSubscribers = useCallback(
    async (authToken) => {
      setFetching(true);
      try {
        const res = await axios.get(`${API}/admin/subscribers`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setData(res.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error("Session expired. Please log in again.");
          logout();
        } else {
          toast.error("Could not load subscribers. Please try again.");
        }
      } finally {
        setFetching(false);
      }
    },
    [logout]
  );

  useEffect(() => {
    if (token) { loadSubscribers(token); loadExtras(token); }
  }, [token, loadSubscribers, loadExtras]);

  const login = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      toast.success("Welcome back, Shaunta!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!data?.subscribers?.length) return;
    const rows = [["Email", "Name", "Signed up (UTC)"]];
    data.subscribers.forEach((s) => {
      rows.push([s.email || "", s.name || "", s.created_at || ""]);
    });
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hannahs-heart-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const announce = async (type) => {
    setAnnouncing(type);
    try {
      const endpointMap = {
        launch: "announce-launch",
        preorder: "announce-preorder",
        launchweek: "announce-launchweek",
      };
      const endpoint = endpointMap[type] || "announce-preorder";
      const res = await axios.post(
        `${API}/admin/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || `Announcement sent to ${res.data.sent} subscriber(s).`);
      setConfirming(null);
      loadSubscribers(token);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Could not send the announcement. Please try again.");
      }
    } finally {
      setAnnouncing(null);
    }
  };

  const forceResendLaunch = async () => {
    if (!window.confirm(
      "Force-send the launch email to ALL subscribers, even those already notified?\n\n" +
      "Some readers may get a duplicate — this is a safety-net for recovering from a botched blast."
    )) return;
    setAnnouncing("launch-force");
    try {
      const res = await axios.post(
        `${API}/admin/announce-launch?force=true`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || `Sent to ${res.data.sent} subscribers.`);
      loadSubscribers(token);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); }
      else toast.error("Could not send. Please try again.");
    } finally {
      setAnnouncing(null);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      toast.error("Please enter an email address.");
      return;
    }
    setSendingTest(true);
    try {
      const res = await axios.post(
        `${API}/admin/send-test`,
        { email: testEmail.trim(), template: testTemplate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.sent) {
        toast.success(res.data.message || `Test email sent to ${res.data.email}.`);
      } else {
        toast.error(res.data.message || "Email service rejected the send.");
      }
    } catch (err) {
      if (err?.response?.status === 401) { logout(); }
      else toast.error(err?.response?.data?.detail || "Could not send test email.");
    } finally {
      setSendingTest(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  // ----- Login screen -----
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-twilight px-6" data-testid="admin-login-screen">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-twilight-surface p-8 shadow-[0_30px_80px_-15px_rgba(0,0,0,0.7)]"
        >
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-gold/15 text-gold">
              <Lock className="h-5 w-5" />
            </div>
            <h1 className="font-display text-2xl font-bold text-cream">Signup Dashboard</h1>
            <p className="mt-1 font-body text-sm text-slate-400">Enter your password to continue</p>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password-input"
            className="w-full rounded-full border border-white/15 bg-white/5 px-5 py-3 text-cream placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:shadow-[0_0_20px_rgba(251,191,36,0.35)]"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-medium text-twilight transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_25px_rgba(251,191,36,0.55)] disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Unlock
          </button>
          <a href="/" className="mt-5 block text-center font-body text-xs text-slate-500 transition-colors hover:text-gold">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  // ----- Dashboard -----
  return (
    <div className="min-h-screen bg-twilight px-6 py-10 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-body text-sm uppercase tracking-[0.25em] text-gold">Hannah&apos;s Heart Awakens</p>
            <h1 className="mt-1 font-display text-3xl font-bold text-cream md:text-4xl">Waitlist Signups</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadSubscribers(token)}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-body text-sm text-cream transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
            >
              <RefreshCw className={`h-4 w-4 ${fetching ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-body text-sm text-cream transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-twilight-surface p-6" data-testid="admin-total-count">
            <div className="flex items-center gap-3 text-gold">
              <Users className="h-5 w-5" />
              <span className="font-body text-sm uppercase tracking-[0.2em] text-slate-400">Book 1 list</span>
            </div>
            <p className="mt-3 font-display text-5xl font-bold text-cream">{data ? data.count : "—"}</p>
          </div>
          <div className="rounded-2xl border border-gold/25 bg-gold/[0.06] p-6" data-testid="admin-book2-count">
            <div className="flex items-center gap-3 text-gold">
              <Mail className="h-5 w-5" />
              <span className="font-body text-sm uppercase tracking-[0.2em] text-slate-400">Book 2 list</span>
            </div>
            <p className="mt-3 font-display text-5xl font-bold text-cream">{data ? (data.book2_count ?? 0) : "—"}</p>
            <p className="mt-1 font-body text-xs text-slate-500">Show this to Kharis</p>
          </div>
          <div className="flex flex-col justify-between rounded-2xl border border-white/10 bg-twilight-surface p-6">
            <div className="flex items-center gap-3 text-gold">
              <Download className="h-5 w-5" />
              <span className="font-body text-sm uppercase tracking-[0.2em] text-slate-400">Export</span>
            </div>
            <button
              onClick={downloadCsv}
              disabled={!data?.subscribers?.length}
              data-testid="admin-export-csv-button"
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 font-medium text-twilight transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_20px_rgba(251,191,36,0.45)] disabled:opacity-40"
            >
              <Download className="h-4 w-4" /> Download CSV
            </button>
          </div>
        </div>

        {/* Tab navigation */}
        <div
          className="mt-8 flex gap-1 overflow-x-auto rounded-full border border-white/10 bg-twilight-surface p-1"
          data-testid="admin-tab-bar"
          role="tablist"
        >
          {[
            { id: "overview", label: "Overview", Icon: LayoutDashboard },
            { id: "broadcasts", label: "Broadcasts", Icon: Mails },
            { id: "blog", label: "Blog", Icon: PenLine },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 font-body text-sm font-medium transition-[background-color,color] duration-300 ${
                tab === id
                  ? "bg-gold text-twilight"
                  : "text-slate-300 hover:text-cream"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab: Overview */}
        {tab === "overview" && (
          <div className="mt-6 space-y-6" data-testid="admin-tab-panel-overview">
            {/* Subscribers list */}
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-twilight-surface">
              <div className="grid grid-cols-12 border-b border-white/10 px-5 py-3 font-body text-xs uppercase tracking-[0.15em] text-slate-400">
                <div className="col-span-7 sm:col-span-8">Email</div>
                <div className="col-span-5 sm:col-span-4 text-right">Signed up</div>
              </div>
              {fetching && !data ? (
                <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                </div>
              ) : data && data.subscribers.length > 0 ? (
                <ul data-testid="admin-subscriber-list">
                  {data.subscribers.map((s, i) => (
                    <li
                      key={`${s.email}-${i}`}
                      data-testid={`admin-subscriber-row-${i}`}
                      className="grid grid-cols-12 items-center border-b border-white/5 px-5 py-3.5 transition-colors duration-200 hover:bg-white/[0.03]"
                    >
                      <div className="col-span-7 flex items-center gap-2 sm:col-span-8">
                        <Mail className="h-3.5 w-3.5 flex-shrink-0 text-gold/70" />
                        <span className="truncate font-body text-sm text-cream">{s.email}</span>
                      </div>
                      <div className="col-span-5 text-right font-body text-xs text-slate-400 sm:col-span-4">
                        {fmt(s.created_at)}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="py-16 text-center" data-testid="admin-empty-state">
                  <p className="font-display text-xl text-cream">No signups yet</p>
                  <p className="mt-2 font-body text-sm text-slate-400">
                    When readers join the waitlist, they&apos;ll appear here.
                  </p>
                </div>
              )}
            </div>

            {/* Messages inbox */}
            <div data-testid="admin-messages-section">
              <div className="mb-3 flex items-center gap-2 text-gold">
                <Inbox className="h-5 w-5" />
                <h2 className="font-display text-xl font-semibold text-cream">
                  Reader Messages{messages.length ? ` · ${messages.length}` : ""}
                </h2>
              </div>
              {messages.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-twilight-surface py-10 text-center">
                  <p className="font-body text-sm text-slate-400">No messages yet.</p>
                </div>
              ) : (
                <ul className="space-y-3" data-testid="admin-messages-list">
                  {messages.map((m, i) => (
                    <li
                      key={i}
                      className="rounded-2xl border border-white/10 bg-twilight-surface p-5"
                      data-testid={`admin-message-${i}`}
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="font-body font-medium text-cream">{m.name}</p>
                        <time className="font-body text-xs text-slate-500">
                          {new Date(m.created_at).toLocaleString()}
                        </time>
                      </div>
                      <a href={`mailto:${m.email}`} className="font-body text-sm text-gold">{m.email}</a>
                      <p className="mt-2 whitespace-pre-wrap font-body text-sm text-slate-300">{m.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Tab: Broadcasts */}
        {tab === "broadcasts" && (
          <div className="mt-6 space-y-4" data-testid="admin-tab-panel-broadcasts">
            {/* Primary broadcasts */}
            <AnnounceCard
              cardTestId="admin-launchweek-card"
              btnPrefix="admin-launchweek"
              Icon={Mail}
              title="Launch Week Reminder"
              description="Gentle follow-up: 'Kindle sale ends July 31'. Perfect nudge for subscribers who missed the launch-day email."
              pending={data ? data.pending_launchweek : 0}
              ctaLabel="Send Launch Week Reminder"
              isConfirming={confirming === "launchweek"}
              onStartConfirm={() => setConfirming("launchweek")}
              onCancel={() => setConfirming(null)}
              onConfirm={() => announce("launchweek")}
              sending={announcing === "launchweek"}
            />

            <AnnounceCard
              cardTestId="admin-launch-card"
              btnPrefix="admin-launch"
              Icon={Rocket}
              title="Launch Day"
              description="The 'It's Launch Day!' email with all buy links + $0.99 note."
              pending={data ? data.pending_launch : 0}
              ctaLabel="Announce Launch Day"
              isConfirming={confirming === "launch"}
              onStartConfirm={() => setConfirming("launch")}
              onCancel={() => setConfirming(null)}
              onConfirm={() => announce("launch")}
              sending={announcing === "launch"}
            />

            <AnnounceCard
              cardTestId="admin-announce-card"
              btnPrefix="admin-announce"
              Icon={Megaphone}
              title="Pre-Order (Archived)"
              description="Original pre-order announcement. Kept here for reference — no longer needed post-launch."
              pending={data ? data.pending_announcement : 0}
              ctaLabel="Announce Pre-Order"
              isConfirming={confirming === "preorder"}
              onStartConfirm={() => setConfirming("preorder")}
              onCancel={() => setConfirming(null)}
              onConfirm={() => announce("preorder")}
              sending={announcing === "preorder"}
            />

            {/* Send Test Email */}
            <div className="rounded-2xl border border-white/10 bg-twilight-surface p-6" data-testid="admin-send-test-card">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-white/5 text-slate-300">
                  <Send className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-display text-lg font-semibold text-cream">Send test email</p>
                  <p className="mt-1 font-body text-sm text-slate-400">
                    Preview what your subscribers see. Bypasses all notification flags.
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="admin-send-test-email"
                      className="rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-cream placeholder:text-slate-500 outline-none transition-[border-color] duration-300 focus:border-gold"
                    />
                    <select
                      value={testTemplate}
                      onChange={(e) => setTestTemplate(e.target.value)}
                      data-testid="admin-send-test-template"
                      className="rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-cream outline-none transition-[border-color] duration-300 focus:border-gold"
                    >
                      <option value="welcome" className="bg-twilight">Welcome</option>
                      <option value="preorder" className="bg-twilight">Pre-Order</option>
                      <option value="launch" className="bg-twilight">Launch Day</option>
                      <option value="launchweek" className="bg-twilight">Launch Week Reminder</option>
                    </select>
                    <button
                      onClick={sendTest}
                      disabled={sendingTest}
                      data-testid="admin-send-test-button"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 font-medium text-twilight transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_20px_rgba(251,191,36,0.45)] disabled:opacity-60"
                    >
                      {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Send test
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Force-resend safety net (advanced) */}
            <details className="group rounded-2xl border border-white/10 bg-twilight-surface" data-testid="admin-force-resend-card">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-6 py-4 font-body text-sm text-slate-300 transition-colors hover:text-cream">
                <span className="inline-flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-slate-400" />
                  Advanced: force-resend launch email
                </span>
                <span className="text-xs text-slate-500 group-open:hidden">Tap to expand</span>
              </summary>
              <div className="border-t border-white/10 px-6 py-5">
                <p className="font-body text-sm text-slate-400">
                  Sends the launch email to <span className="text-cream">all {data?.count ?? "—"} subscribers</span>, ignoring the "already notified" flag. Use only if you suspect people were silently skipped. Some readers may get a duplicate.
                </p>
                <button
                  onClick={forceResendLaunch}
                  disabled={announcing === "launch-force" || !data?.count}
                  data-testid="admin-force-resend-button"
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 bg-transparent px-6 py-3 font-medium text-gold transition-[background-color,color] duration-300 hover:bg-gold hover:text-twilight disabled:opacity-40"
                >
                  {announcing === "launch-force" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                  Resend to everyone
                </button>
              </div>
            </details>
          </div>
        )}

        {/* Tab: Blog */}
        {tab === "blog" && (
          <div className="mt-6" data-testid="admin-tab-panel-blog">
            <div className="rounded-2xl border border-white/10 bg-twilight-surface p-6">
              <p className="font-body text-sm text-slate-400">
                Formatting tips:
                <br />
                • Blank line between paragraphs
                <br />
                • <span className="text-gold">## </span> at the start of a line = subheading
                <br />
                • <span className="text-gold">&gt; </span> at the start of a line = quote
                <br />
                • Wrap text in <span className="text-gold">*asterisks*</span> for <em>italics</em> (e.g., <span className="text-cream">*Hannah&apos;s Heart Awakens*</span>)
                <br />
                • Wrap text in <span className="text-gold">**double asterisks**</span> for <strong>bold</strong>
              </p>
              <input
                value={newPost.title}
                onChange={(e) => setNewPost((p) => ({ ...p, title: e.target.value }))}
                placeholder="Post title"
                data-testid="blog-title-input"
                className="mt-4 w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-cream placeholder:text-slate-500 outline-none transition-[border-color] duration-300 focus:border-gold"
              />
              <textarea
                rows={7}
                value={newPost.body}
                onChange={(e) => setNewPost((p) => ({ ...p, body: e.target.value }))}
                placeholder="Write your post here…"
                data-testid="blog-body-input"
                className="mt-3 w-full resize-y rounded-xl border border-white/15 bg-white/5 px-4 py-3 font-body text-cream placeholder:text-slate-500 outline-none transition-[border-color] duration-300 focus:border-gold"
              />
              <label
                className="mt-4 flex cursor-pointer items-center gap-3 font-body text-sm text-slate-300"
                data-testid="blog-notify-label"
              >
                <input
                  type="checkbox"
                  checked={notifyOnPublish}
                  onChange={(e) => setNotifyOnPublish(e.target.checked)}
                  data-testid="blog-notify-checkbox"
                  className="h-4 w-4 accent-gold"
                />
                Email my subscribers about this post
                {data ? <span className="text-gold">({data.count} on list)</span> : null}
              </label>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setPreviewMode((p) => !p)}
                  disabled={!newPost.title && !newPost.body}
                  data-testid="blog-preview-toggle"
                  className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-transparent px-5 py-2.5 font-medium text-gold transition-[background-color,color] duration-300 hover:bg-gold hover:text-twilight disabled:opacity-40"
                >
                  {previewMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {previewMode ? "Back to editing" : "Preview post"}
                </button>
                <button
                  onClick={createPost}
                  disabled={posting}
                  data-testid="blog-publish-button"
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-medium text-twilight transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_20px_rgba(251,191,36,0.45)] disabled:opacity-60"
                >
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
                  Publish Post
                </button>
              </div>

              {previewMode && (newPost.title || newPost.body) && (
                <div
                  data-testid="blog-preview-panel"
                  className="mt-6 rounded-2xl border border-gold/25 bg-twilight px-6 py-8 md:px-10"
                >
                  <p className="mb-4 font-body text-xs uppercase tracking-[0.2em] text-gold">
                    Preview · exactly how readers will see it
                  </p>
                  {newPost.title && (
                    <h1 className="font-display text-3xl font-bold leading-tight text-cream md:text-4xl">
                      {renderInline(newPost.title)}
                    </h1>
                  )}
                  {newPost.title && <p className="mt-3 font-body text-sm text-slate-400">by Shaunta Scott</p>}
                  {newPost.title && <div className="my-6 h-px w-full bg-white/10" />}
                  {newPost.body && <PostBody body={newPost.body} />}
                </div>
              )}
            </div>

            {posts.length > 0 && (
              <>
                <h3 className="mt-6 mb-3 font-body text-sm uppercase tracking-[0.2em] text-slate-400">Published posts</h3>
                <ul className="space-y-2" data-testid="admin-blog-list">
                  {posts.map((p) => (
                    <li
                      key={p.slug}
                      className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-3.5"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-body text-sm text-cream">{p.title}</p>
                        <p className="font-body text-xs text-slate-500">
                          {new Date(p.published_at).toLocaleDateString()} · {p.views || 0} {(p.views || 0) === 1 ? "read" : "reads"}
                        </p>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-3">
                        <a
                          href={`/blog/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-body text-xs text-slate-400 transition-colors hover:text-gold"
                        >
                          View
                        </a>
                        <button
                          onClick={() => resendBlogNotify(p)}
                          disabled={notifyingPostId === p.id}
                          data-testid={`blog-notify-${p.slug}`}
                          aria-label="Notify subscribers"
                          title="Re-send notification to all subscribers"
                          className="inline-flex items-center gap-1 text-xs text-slate-400 transition-colors duration-300 hover:text-gold disabled:opacity-50"
                        >
                          {notifyingPostId === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Send className="h-3.5 w-3.5" />
                          )}
                          Notify
                        </button>
                        <button
                          onClick={() => deletePost(p.id)}
                          data-testid={`blog-delete-${p.slug}`}
                          aria-label="Delete post"
                          className="text-slate-500 transition-colors duration-300 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        <a href="/" className="mt-10 block text-center font-body text-xs text-slate-500 transition-colors hover:text-gold">
          ← Back to website
        </a>
      </div>
    </div>
  );
}
